import { api } from '../../lib/api';
import type { BotBusinessHours, BotCapabilities, BotConfig, BotFollowUps, BotConversation, BotConversationDetail, BotKnowledgeBase, BotLeadSource, BotMessage, BotMode, BotRunawayCaps, BotSendOutcome, HandoffTrigger } from '../../types/leadBot';
import type { LeadOutcome } from '../../types/leadOutcome.js';
import type { BotTesterInviteRow } from '../../types/botTesterInvite';
import type { BoothStats } from '../../lib/fitnessBot/boothStats';
import type {
  ListingStockSummary,
  ListingUploadResult,
  MappedListingRow,
  VehicleListing,
} from '../../types/vehicleListing';

// SMS Lead Bot Phase 0 — the authed staff API.

export async function getBoothStats(botId: string, accounts?: string): Promise<BoothStats & { liveInviteUrl: string | null }> {
  const q = accounts ? `?accounts=${encodeURIComponent(accounts)}` : '';
  return api<BoothStats & { liveInviteUrl: string | null }>(`/api/lead-bot/live/${encodeURIComponent(botId)}${q}`);
}

export async function resetDemo(
  botId: string,
): Promise<{ conversationsClosed: number; appointmentsCancelled: number }> {
  return api(`/api/lead-bot/configs/${encodeURIComponent(botId)}/reset-demo`, { method: 'POST' });
}

export type LeadBotStatus = {
  smsConfigured: boolean;
  autonomousSendingBuilt: boolean;
  /**
   * ⚠️ FALSE DURING THE TRIAL — the bot shares the broadcast number, which is
   * safe only because the allowlist bounds who can be texted at all.
   */
  dedicatedNumber: boolean;
};

export async function getLeadBotStatus(): Promise<LeadBotStatus> {
  return api<LeadBotStatus>('/api/lead-bot/status');
}

export async function listConversations(): Promise<{
  conversations: BotConversation[];
  pendingConversationIds: string[];
}> {
  return api<{ conversations: BotConversation[]; pendingConversationIds: string[] }>(
    '/api/lead-bot/conversations',
  );
}

export async function getConversation(id: string): Promise<BotConversationDetail> {
  return api<BotConversationDetail>(`/api/lead-bot/conversations/${id}`);
}

export type CreateLeadArgs = {
  accountId: string;
  leadName: string;
  leadPhone: string;
  leadEmail: string | null;
  source: BotLeadSource;
  campaign: string | null;
};

export async function createLead(args: CreateLeadArgs): Promise<{
  conversation: BotConversation;
  draft: BotMessage | null;
  draftSkipped: { reason: string; detail: string } | null;
  sent: boolean;
  /** Why an autonomous opening message did not go out — chiefly "not allowlisted". */
  sendBlocked: string | null;
}> {
  return api('/api/lead-bot/conversations', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function regenerateDraft(conversationId: string): Promise<unknown> {
  return api(`/api/lead-bot/conversations/${conversationId}/regenerate`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function handOffConversation(
  conversationId: string,
  note: string | null,
): Promise<{ conversation: BotConversation }> {
  return api(`/api/lead-bot/conversations/${conversationId}/handoff`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

/**
 * Give a handed-over conversation BACK to the assistant (audited). After a handover the assistant
 * is quiet; this is the only way it speaks again.
 */
export async function handBackConversation(
  conversationId: string,
): Promise<{ conversation: BotConversation }> {
  return api(`/api/lead-bot/conversations/${conversationId}/hand-back`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * ⚠️ The outcome rides the close so an AM classifies where they already are.
 * Optional — a close is never blocked on it; an unclassified conversation is a
 * reported gap, not a refused action.
 */
export async function closeConversation(
  conversationId: string,
  outcome?: LeadOutcome,
): Promise<{ conversation: BotConversation }> {
  return api(`/api/lead-bot/conversations/${conversationId}/close`, {
    method: 'POST',
    body: JSON.stringify(outcome === undefined ? {} : { outcome }),
  });
}

/**
 * Record what came of a conversation, at any time. ⚠️ It cannot be backfilled
 * from anything we store, so it stays settable long after the conversation ends
 * — the lead turning up is news that usually arrives later.
 */
export async function setConversationOutcome(
  conversationId: string,
  outcome: LeadOutcome,
): Promise<{ conversation: BotConversation }> {
  return api(`/api/lead-bot/conversations/${conversationId}/outcome`, {
    method: 'POST',
    body: JSON.stringify({ outcome }),
  });
}

/**
 * ⚠️ THE ONLY CALL THAT CAN CAUSE AN SMS TO A MEMBER OF THE PUBLIC.
 *
 * It resolves rather than throwing on `ok: false` — an unprovisioned number, a
 * runaway cap or an opt-out are ANSWERS, not failures, and the queue renders
 * the reason with the draft still in place.
 */
export async function approveDraft(
  messageId: string,
  text?: string,
): Promise<BotSendOutcome> {
  return api<BotSendOutcome>(`/api/lead-bot/drafts/${messageId}/approve`, {
    method: 'POST',
    body: JSON.stringify(text === undefined ? {} : { text }),
  });
}

export async function rejectDraft(messageId: string): Promise<{ message: BotMessage }> {
  return api(`/api/lead-bot/drafts/${messageId}/reject`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function listBotConfigs(): Promise<BotConfig[]> {
  const res = await api<{ configs: BotConfig[] }>('/api/lead-bot/configs');
  return res.configs;
}

export type BotConfigArgs = {
  accountId: string;
  name: string;
  mode: BotMode;
  isActive: boolean;
  assistantName: string;
  goal: string;
  tone: string;
  /** ⚠️ Not cosmetic — decides which of the two handoff modes applies, and the
   *  two produce different WORDS. See BusinessHoursEditor. */
  businessHours: BotBusinessHours;
  /** ⚠️ The guard the autonomous trial rests on — see AllowlistEditor. */
  testPhoneAllowlist: string[];
  knowledgeSupplement: string;
  /** Where a handoff is paged. Optional; a handoff is recorded either way. */
  forwardingNumber: string | null;
  handoffContactName: string | null;
  handoffPhone: string | null;
  handoffEmail: string | null;
  /**
   * ⚠️ Runaway caps. Positive integers within bounds — there is deliberately no
   * "unlimited", and blank/zero is refused rather than meaning off.
   */
  caps: BotRunawayCaps;
  /** Outbound turns before a stuck conversation is handed to a person. */
  maxTurnsWithoutProgress: number;
  // ---- fitness vertical (all optional; absent = an automotive bot) ----
  vertical?: 'automotive' | 'fitness';
  playbook?: 'new_lead_engagement';
  disclosureAnswer?: string;
  signOff?: string;
  capabilities?: BotCapabilities;
  callForwardNumber?: string | null;
  followUps?: BotFollowUps;
  /** Kyck-only "Instructions for Sam". Staff pages only: never the client portal. */
  instructions?: string;
  isDemo?: boolean;
  demoExpiresAt?: string | null;
  demoTimeScale?: number | null;
};

export async function createBotConfig(args: BotConfigArgs): Promise<BotConfig> {
  const res = await api<{ config: BotConfig }>('/api/lead-bot/configs', {
    method: 'POST',
    body: JSON.stringify(args),
  });
  return res.config;
}

export async function updateBotConfig(
  id: string,
  patch: Partial<Omit<BotConfigArgs, 'accountId'>>,
): Promise<BotConfig> {
  const res = await api<{ config: BotConfig }>(`/api/lead-bot/configs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return res.config;
}

export async function listKnowledgeBases(): Promise<BotKnowledgeBase[]> {
  const res = await api<{ knowledgeBases: BotKnowledgeBase[] }>(
    '/api/lead-bot/knowledge-bases',
  );
  return res.knowledgeBases;
}

export async function getKnowledgeBase(accountId: string): Promise<BotKnowledgeBase | null> {
  const res = await api<{ knowledgeBase: BotKnowledgeBase | null }>(
    `/api/lead-bot/knowledge-bases/${accountId}`,
  );
  return res.knowledgeBase;
}

export type KnowledgeBaseArgs = Pick<
  BotKnowledgeBase,
  | 'businessName'
  | 'about'
  | 'addresses'
  | 'hours'
  | 'brandsStocked'
  | 'services'
  | 'financePolicy'
  | 'tradeInPolicy'
  | 'warranty'
  | 'viewingProcess'
  | 'extraNotes'
>;

export async function saveKnowledgeBase(
  accountId: string,
  args: KnowledgeBaseArgs,
): Promise<BotKnowledgeBase> {
  const res = await api<{ knowledgeBase: BotKnowledgeBase }>(
    `/api/lead-bot/knowledge-bases/${accountId}`,
    { method: 'PUT', body: JSON.stringify(args) },
  );
  return res.knowledgeBase;
}

/**
 * Run the deterministic handoff detector over arbitrary text. Read-only, and
 * there so the team can SEE what the compliance net catches — and what it
 * misses — while tuning it, rather than finding out from a live transcript.
 */
export async function detectTriggers(text: string): Promise<HandoffTrigger[]> {
  const res = await api<{ triggers: HandoffTrigger[] }>('/api/lead-bot/detect-triggers', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
  return res.triggers;
}

// ---------------------------------------------------------------------------
// vehicle stock (Phase 1)
// ---------------------------------------------------------------------------

export async function getStock(
  accountId: string,
): Promise<{ summary: ListingStockSummary; listings: VehicleListing[] }> {
  return api(`/api/lead-bot/stock/${accountId}`);
}

/**
 * ⚠️ REPLACES the account's stock set. Rows arrive already mapped onto field
 * keys — the column mapping is a client-side step, done where the AM can see
 * the file and fix a wrong guess.
 */
export async function uploadStock(
  accountId: string,
  rows: MappedListingRow[],
): Promise<ListingUploadResult> {
  return api<ListingUploadResult>(`/api/lead-bot/stock/${accountId}`, {
    method: 'POST',
    body: JSON.stringify({ rows }),
  });
}

// ---------------------------------------------------------------------------
// tester invites (staff side)
// ---------------------------------------------------------------------------
//
// ⚠️ Issuing and revoking only. REDEEMING is the public route, and there is
// deliberately no authed "add this number to the allowlist" call here — the
// settings form already edits the allowlist directly, and a second write path
// into the guard is a second place to get it wrong.

export async function listTesterInvites(botConfigId: string): Promise<BotTesterInviteRow[]> {
  const res = await api<{ invites: BotTesterInviteRow[] }>(
    `/api/lead-bot/configs/${botConfigId}/invites`,
  );
  return res.invites;
}

export async function createTesterInvite(args: {
  botConfigId: string;
  label: string;
  maxUses: number;
  expiresAt: string;
}): Promise<BotTesterInviteRow> {
  const res = await api<{ invite: BotTesterInviteRow }>('/api/lead-bot/invites', {
    method: 'POST',
    body: JSON.stringify(args),
  });
  return res.invite;
}

export async function revokeTesterInvite(id: string): Promise<BotTesterInviteRow> {
  const res = await api<{ invite: BotTesterInviteRow }>(
    `/api/lead-bot/invites/${id}/revoke`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return res.invite;
}

// ---------------------------------------------------------------------------
// reporting
// ---------------------------------------------------------------------------

/**
 * ⚠️ RAW DOCUMENTS, aggregated on the client. Filters then cost no round trip,
 * and every honesty rule lives in the pure `src/lib/leadBot/reporting.ts` where
 * it is unit-testable. See the store's `readAllMessages` for the scale note.
 */
export async function getReportData(): Promise<{
  conversations: BotConversation[];
  messages: BotMessage[];
  configs: BotConfig[];
}> {
  return api('/api/lead-bot/report-data');
}

/** The do-not-contact flag for a conversation's lead at that conversation's club (audited both ways). */
export interface DoNotContactState {
  doNotContact: boolean;
  record: { setAt: string; setByName: string | null; note: string | null } | null;
}

export async function getDoNotContact(conversationId: string): Promise<DoNotContactState> {
  return api(`/api/lead-bot/conversations/${conversationId}/do-not-contact`);
}

export async function setDoNotContactFlag(conversationId: string, set: boolean): Promise<{ doNotContact: boolean }> {
  return api(`/api/lead-bot/conversations/${conversationId}/do-not-contact`, {
    method: 'POST',
    body: JSON.stringify({ set }),
  });
}
